import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { setJsonLd, setPageMeta } from "@/lib/api";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { BuyBox, DropNotify } from "@/components/BuyBox";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Motion";
import { productByUrlSlug, collectionByPath, SHOP_COLLECTIONS } from "@/lib/shopCatalog";
import { trackViewItem } from "@/lib/analytics";
import { useStockOverrides, applyStock, useDropDates } from "@/lib/stock";
import { DropCountdown } from "@/components/DropCountdown";
import { OfferCapture } from "@/components/OfferCapture";
import { ExitIntentOffer } from "@/components/ExitIntentOffer";
import { BundleBuilder } from "@/components/BundleBuilder";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { pushRecent } from "@/lib/recentlyViewed";
import NotFound from "./NotFound";

const DetailBlock = ({ label, children, testId }) => (
  <details className="border-t border-white/10 py-4 group" data-testid={testId}>
    <summary className="font-mono-label text-white/70 cursor-pointer list-none flex justify-between items-center">
      {label}
      <span className="text-white/40 group-open:rotate-45 transition-transform duration-200">+</span>
    </summary>
    <div className="text-white/60 text-sm mt-3 space-y-2 leading-relaxed">{children}</div>
  </details>
);

export default function ProductPage() {
  const { productSlug } = useParams();
  const [searchParams] = useSearchParams();
  const presetSize = searchParams.get("size");
  const overrides = useStockOverrides();
  const dropDates = useDropDates();
  const p = applyStock(productByUrlSlug(productSlug), overrides);
  const [showBack, setShowBack] = useState(false);

  useEffect(() => {
    if (!p) return;
    setShowBack(false);
    window.scrollTo(0, 0);
    setPageMeta(p.title, p.meta_description, {
      canonicalPath: `/products/${p.url_slug}`, type: "product", image: p.image, imageAlt: p.alt,
    });
    trackViewItem(p);
    pushRecent(p.slug);
    return setJsonLd("product-jsonld", p.jsonld);
  }, [productSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!p) return <NotFound />;

  const related = p.related.map(productByUrlSlug).filter(Boolean);
  const parentCol = SHOP_COLLECTIONS.find((c) => c.slug === p.primary_collection);
  const cityCol = p.city_slug ? collectionByPath(`/shop/city-series/${p.city_slug}`) : null;
  const available = p.status === "available";

  return (
    <div className="bg-night min-h-screen" data-testid="product-page">
      <div className="px-5 lg:px-10 pt-8">
        <Breadcrumbs light items={p.breadcrumbs} />
      </div>
      <div className="px-5 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <Reveal>
          <div className="lg:sticky lg:top-24">
            <div
              className={`border border-white/10 aspect-square relative overflow-hidden${p.image_hover ? " cursor-pointer" : ""}`}
              onClick={() => p.image_hover && setShowBack((v) => !v)}
              role={p.image_hover ? "button" : undefined}
              aria-label={p.image_hover ? "Flip between front and back view" : undefined}
              data-testid="product-gallery"
            >
              <img
                src={p.image}
                alt={p.alt}
                width="1024"
                height="1024"
                fetchPriority="high"
                className={`${p.color_art ? "" : "img-bw "}h-full w-full object-cover transition-opacity duration-300${showBack ? " opacity-0" : ""}`}
                data-testid="product-image"
              />
              {p.image_hover && (
                <img
                  src={p.image_hover}
                  alt={`${p.seo_name} — back view`}
                  width="1024"
                  height="1024"
                  loading="lazy"
                  className={`${p.color_art ? "" : "img-bw "}absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${showBack ? "opacity-100" : "opacity-0"}`}
                  data-testid="product-image-back"
                />
              )}
              {p.image_hover && (
                <span
                  className="absolute bottom-3 right-3 font-mono-label bg-ink/80 text-white/90 border border-white/20 px-3 py-2 backdrop-blur-sm"
                  data-testid="product-view-toggle"
                >
                  {showBack ? "← VIEW FRONT" : "VIEW BACK →"}
                </span>
              )}
            </div>
            {related[0] && (
              <Link
                to={`/products/${related[0].url_slug}`}
                data-testid="product-pairs-with"
                className="mt-px grid grid-cols-[96px,1fr] items-center gap-4 border border-white/10 p-3 group hover:border-white/40 transition-colors duration-200"
              >
                <span className="block aspect-square overflow-hidden border border-white/10">
                  <img
                    src={related[0].image}
                    alt={related[0].alt || related[0].seo_name}
                    loading="lazy"
                    className={`${related[0].color_art ? "" : "img-bw "}h-full w-full object-cover transition-transform duration-500 group-hover:scale-105`}
                  />
                </span>
                <span>
                  <span className="font-mono-label text-white/40 block">PAIRS WITH</span>
                  <span className="font-display text-lg block mt-1 leading-tight uppercase">{related[0].seo_name}</span>
                  <span className="text-white/50 text-sm block mt-1">${related[0].price}</span>
                </span>
              </Link>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-mono-label text-white/40">{p.classification?.toUpperCase()}</p>
          <h1 data-testid="product-title" className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none uppercase">{p.seo_name}</h1>
          <div className="flex items-center gap-4 mt-5">
            <p data-testid="product-price" className="font-display text-2xl">${p.price}</p>
            <p data-testid="product-availability" className="font-mono-label text-white/50 border border-white/20 px-3 py-1.5">
              {available ? "IN STOCK" : "WAITLIST"}
            </p>
            {p.drop_size && <p className="font-mono-label text-white/40">LIMITED RUN OF {p.drop_size}</p>}
          </div>
          <p className="text-white/60 text-sm mt-5 leading-relaxed" data-testid="product-description">{p.long_description}</p>

          {available && p.price ? (
            <BuyBox product={p} large initialSize={presetSize} />
          ) : (
            <>
              <DropCountdown dropAt={dropDates[p.slug]} slug={p.slug} />
              <DropNotify product={p} />
            </>
          )}

          {["skyline", "area", "color"].includes(p.kind) && available && (
            <button
              type="button"
              onClick={() => document.getElementById("product-bundle-section")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="bundle-nudge"
              className="mt-4 w-full flex items-center justify-between gap-4 border border-dashed border-white/25 px-4 py-3.5 text-left hover:border-white/60 transition-colors duration-200 group"
            >
              <span>
                <span className="font-mono-label text-white/40 block">BUNDLE &amp; SAVE</span>
                <span className="text-sm text-white/70 block mt-1">Add any second city tee — both ship for $75 flat.</span>
              </span>
              <span className="font-mono-label text-white/60 group-hover:text-white shrink-0">PAIR IT ↓</span>
            </button>
          )}

          <div className="mt-8 border-b border-white/10">
            <DetailBlock label="MATERIAL, FIT & CARE" testId="product-material-block">
              {p.material && <p><span className="text-white/40">MATERIAL — </span>{p.material}</p>}
              <p><span className="text-white/40">FIT — </span>{p.fit}</p>
              <p><span className="text-white/40">CARE — </span>{p.care}</p>
              <p><span className="text-white/40">COLOR — </span>{p.color}</p>
              <p><span className="text-white/40">SKU — </span>{p.sku}</p>
            </DetailBlock>
            <DetailBlock label="SIZE & FIT" testId="product-size-block">
              <p>{p.fit}</p>
              <p>Sizes offered: {(p.sizes || []).join(", ")}.</p>
              <p><Link to="/size-guide" data-testid="product-size-guide-link" className="underline underline-offset-4 hover:text-white">Full size guide →</Link></p>
            </DetailBlock>
            <DetailBlock label="SHIPPING & RETURNS" testId="product-shipping-block">
              <p>{p.shipping_note}</p>
              <p>{p.returns_note}</p>
              <p>
                <Link to="/shipping" className="underline underline-offset-4 hover:text-white">Shipping policy</Link>
                {" · "}
                <Link to="/returns" data-testid="product-returns-link" className="underline underline-offset-4 hover:text-white">Return policy</Link>
              </p>
            </DetailBlock>
          </div>

          <div className="mt-8" data-testid="product-story">
            <p className="font-mono-label text-white/40">THE STORY</p>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{p.story}</p>
          </div>

          <div className="mt-8 flex flex-wrap gap-2" data-testid="product-collection-links">
            {parentCol && (
              <Link to={parentCol.path} className="font-mono-label border border-white/20 px-4 py-2.5 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200">
                {parentCol.h1.toUpperCase()}
              </Link>
            )}
            {cityCol && (
              <>
                <Link to={cityCol.path} data-testid="product-city-collection-link" className="font-mono-label border border-white/20 px-4 py-2.5 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200">
                  {cityCol.city_name.toUpperCase()} RUNNING APPAREL
                </Link>
                {cityCol.directory_path && (
                  <Link to={cityCol.directory_path} data-testid="product-city-directory-link" className="font-mono-label border border-white/20 px-4 py-2.5 text-white/70 hover:bg-white hover:text-ink transition-colors duration-200">
                    {cityCol.city_name.toUpperCase()} RUN CLUBS
                  </Link>
                )}
              </>
            )}
          </div>
        </Reveal>
      </div>

      <section className="px-5 lg:px-10 py-12 border-t border-white/10" data-testid="product-faq-section">
        <h2 className="font-display text-3xl lg:text-4xl">QUESTIONS, ANSWERED.</h2>
        <div className="mt-6 max-w-3xl">
          {p.faqs.map((f, i) => (
            <details key={f.q} data-testid={`product-faq-${i}`} className="border-t border-white/10 py-4 group">
              <summary className="font-mono-label text-white/80 cursor-pointer list-none flex justify-between items-center gap-4">
                {f.q}
                <span className="text-white/40 group-open:rotate-45 transition-transform duration-200 shrink-0">+</span>
              </summary>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {["skyline", "area", "color"].includes(p.kind) && (
        <section id="product-bundle-section" className="px-5 lg:px-10 py-12 border-t border-white/10" data-testid="product-bundle-section">
          <BundleBuilder defaultSlug={p.slug} />
        </section>
      )}

      {related.length > 0 && (
        <section className="px-5 lg:px-10 py-12 border-t border-white/10" data-testid="product-related-section">
          <h2 className="font-display text-3xl lg:text-4xl">RUNS WELL WITH.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-8">
            {related.map((rp) => (
              <ProductCard key={rp.slug} p={rp} listId={`related-${p.slug}`} listName={`Related: ${p.seo_name}`} />
            ))}
          </div>
        </section>
      )}

      <RecentlyViewed excludeSlug={p.slug} />

      <section className="px-5 lg:px-10 py-12 border-t border-white/10">
        <OfferCapture source={`product-${p.slug}`} />
      </section>
      <ExitIntentOffer source={`exit-product-${p.slug}`} />
    </div>
  );
}
